import React from 'react';
import { Button, CircularProgress, styled } from '@mui/material';

/**
 * Form button aliases — thin wrappers around MUI Button styled via theme.js MuiButton.
 * Prefer plain `<Button variant="..." color="...">` or these aliases for common intents.
 *
 * Usage:
 *   import { PrimaryButton, SecondaryButton, TextButton } from './common/FormButtons';
 *
 *   <PrimaryButton onClick={handleSubmit}>Submit</PrimaryButton>
 *   <SecondaryButton startIcon={<ArrowBack />}>Back</SecondaryButton>
 *   <TextButton>Cancel</TextButton>
 */

const LARGE_SIZE_SX = {
  padding: '10px 24px',
  fontSize: '15px',
};

const CONTAINED_LOADING_VARIANTS = new Set(['primary', 'danger', 'success']);

const resolveSizeProps = (size = 'medium', sx) => {
  if (size === 'large') {
    return { size: 'medium', sx: sx ? { ...LARGE_SIZE_SX, ...sx } : LARGE_SIZE_SX };
  }
  return { size, sx };
};

const ThemeButton = ({ variant, color, size = 'medium', sx, ...props }) => {
  const sizeProps = resolveSizeProps(size, sx);
  return <Button variant={variant} color={color} {...sizeProps} {...props} />;
};

/** Primary CTA — contained ink */
export const PrimaryButton = (props) => (
  <ThemeButton variant="contained" color="primary" {...props} />
);

/** Secondary action — outlined ghost */
export const SecondaryButton = (props) => (
  <ThemeButton variant="outlined" color="primary" {...props} />
);

/** Neutral / dismissive action — same ghost chrome as secondary */
export const NeutralButton = (props) => (
  <ThemeButton variant="outlined" color="primary" {...props} />
);

/** Tertiary text action */
export const TextButton = (props) => (
  <ThemeButton variant="text" color="primary" {...props} />
);

/** Destructive filled action */
export const DangerButton = (props) => (
  <ThemeButton variant="contained" color="error" {...props} />
);

/** Destructive ghost action */
export const DangerOutlinedButton = (props) => (
  <ThemeButton variant="outlined" color="error" {...props} />
);

/** Positive confirmation action */
export const SuccessButton = (props) => (
  <ThemeButton variant="contained" color="success" {...props} />
);

/**
 * Loading Button wrapper - Add loading state to any button
 *
 * Usage:
 *   <LoadingButton loading={isLoading} onClick={handleClick}>
 *     Submit
 *   </LoadingButton>
 */
export const LoadingButton = ({
  loading,
  children,
  disabled,
  variant = 'primary',
  size = 'medium',
  ...props
}) => {
  const ButtonComponent =
    {
      primary: PrimaryButton,
      secondary: SecondaryButton,
      neutral: NeutralButton,
      text: TextButton,
      danger: DangerButton,
      success: SuccessButton,
    }[variant] || PrimaryButton;

  const spinnerSize = size === 'small' ? 16 : size === 'large' ? 22 : 18;

  return (
    <ButtonComponent disabled={disabled || loading} size={size} {...props}>
      {loading ? (
        <CircularProgress
          size={spinnerSize}
          color={CONTAINED_LOADING_VARIANTS.has(variant) ? 'inherit' : 'primary'}
        />
      ) : (
        children
      )}
    </ButtonComponent>
  );
};

/** Button Group Container - For consistent spacing between buttons */
export const ButtonGroup = styled('div')({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  flexWrap: 'wrap',
});

/** Button Group with right alignment - Common for dialog actions */
export const ButtonGroupRight = styled('div')({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
});

/** Button Group with space between - Cancel on left, actions on right */
export const ButtonGroupSpaced = styled('div')({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  width: '100%',
});

export default {
  PrimaryButton,
  SecondaryButton,
  NeutralButton,
  TextButton,
  DangerButton,
  DangerOutlinedButton,
  SuccessButton,
  LoadingButton,
  ButtonGroup,
  ButtonGroupRight,
  ButtonGroupSpaced,
};
